import { KindResultDecisionCodesEnum } from 'APIPackage/CatalogsModule/enums/KindResultDecisionCodesEnum';
import { KnmResultDecisionStatusEnum } from 'APIPackage/KnmModule/enums/KnmResultDecisionStatusEnum';
import { IKnm } from 'APIPackage/KnmModule/interfaces/IKnm';
import { IKnmResultDecision248 } from 'APIPackage/KnmModule/interfaces/IKnmResultDecision248';
import { IKnmSubject248 } from 'APIPackage/KnmModule/interfaces/IKnmSubject248';
import { IPmResultDecision } from 'APIPackage/KnmModule/interfaces/IPmResultDecision';
import { parseDate } from 'CorePackage/MainModule/common/formatDate';
import { DATE_FORMAT_LONG_FNS } from 'CorePackage/MainModule/constants/dateFormats';
import { generateOrganizationTemplateText } from 'KnmErknmPackage/MainModule/common/generateOrganizationTemplateText';
import { ISelectOption } from 'UIPackage/MainModule/components/SelectInput';

import { checkViolationForMatchingRepeatCheck } from '../../../functions/getMatchedViolation';

/** Имя поля формы с выбранными предписаниями */
export const LINK_PRESCRIPTIONS_FIELD = 'linkPrescriptions';

/**
 * Предписание связанного паспорта: решение по результатам КНМ (IKnmResultDecision248)
 * либо решение ПМ (IPmResultDecision). Номер и дата у них называются по-разному.
 */
export type TPrescriptionDecision = IKnmResultDecision248 | IPmResultDecision;

/** Строка поля "Предписание": undefined — строка добавлена, но предписание в ней ещё не выбрано */
export type TPrescriptionRow = ISelectOption | undefined;

/** Предписание вместе с контролируемым лицом, которому оно выдано */
export interface ILinkedPrescription {
  decision: IKnmResultDecision248;
  organization: IKnmSubject248;
}

const PRESCRIPTION_KIND_DECISION_CODES: KindResultDecisionCodesEnum[] = [
  KindResultDecisionCodesEnum.D_1,
  KindResultDecisionCodesEnum.D_7,
];

export const isPrescriptionKindDecisionCode = (code?: KindResultDecisionCodesEnum | string | null) =>
  PRESCRIPTION_KIND_DECISION_CODES.some(prescriptionCode => prescriptionCode === code);

export const isPrescription = (decision: TPrescriptionDecision) =>
  isPrescriptionKindDecisionCode(decision.kindDecision?.code);

export const isPrescriptionNotCompleted = (decision: TPrescriptionDecision, isEnablePrescriptionCheck: boolean) =>
  isEnablePrescriptionCheck
    ? Boolean(decision.signed) && decision.status === KnmResultDecisionStatusEnum.NOT_COMPLETED
    : decision.status === KnmResultDecisionStatusEnum.NOT_COMPLETED;

/**
 * У предписания есть нарушения, подлежащие повторной проверке:
 * статус "Не устранено"/"Ожидает подтверждения" и срок устранения наступил к дате начала мероприятия.
 */
export const hasViolationsForRepeatCheck = (decision: TPrescriptionDecision, startDate?: Date) =>
  Boolean(
    decision.status !== KnmResultDecisionStatusEnum.CANCELED &&
      decision.violations?.some(violation =>
        violation.structuralUnit?.mandatoryRequirementsViolations?.some(mandatory =>
          mandatory.violations?.some(viol => checkViolationForMatchingRepeatCheck(viol, startDate)),
        ),
      ),
  );

/**
 * Идентификатор предписания внутри связанного паспорта.
 */
export const getPrescriptionKey = (decision: TPrescriptionDecision) => decision.guid ?? '';

const getPrescriptionNumber = (decision: TPrescriptionDecision) =>
  'numberDecision' in decision ? decision.numberDecision : decision.number;

/** Формат значения по постановке: Решение <номер> КЛ: <контролируемое лицо> */
const getPrescriptionLabel = ({ decision, organization }: ILinkedPrescription) => {
  const number = getPrescriptionNumber(decision);
  const subjectText = generateOrganizationTemplateText(
    organization.inn || undefined,
    organization.ogrn || undefined,
    organization.organizationName || undefined,
    organization.rafpNza || undefined,
  );

  return `Решение${number ? ` ${number}` : ''}${subjectText ? ` КЛ: ${subjectText}` : ''}`;
};

/**
 * Предписания КНМ лежат в решениях по результатам каждого контролируемого лица
 * (knmErknm.organizations[].resultDecisions[])
 * У рейдового осмотра возможно добавление нескольких лиц.
 */
export const getErknmPrescriptions = (knm: IKnm): ILinkedPrescription[] => {
  const organizations: IKnmSubject248[] = knm.knmErknm?.organizations ?? [];
  const prescriptions: ILinkedPrescription[] = [];

  organizations.forEach(organization => {
    (organization.resultDecisions ?? []).forEach(decision => {
      if (isPrescription(decision)) {
        prescriptions.push({ decision, organization });
      }
    });
  });

  return prescriptions;
};

/**
 * Предписания, по которым можно проводить повторную проверку:
 * предписание не исполнено и по нему есть нарушения с наступившим сроком устранения.
 */
export const getPrescriptionsForRepeatCheck = (
  knm: IKnm,
  isEnablePrescriptionCheck: boolean,
  erknmStartDateString?: string,
): ILinkedPrescription[] => {
  const startDate = parseDate({ date: erknmStartDateString || new Date(), parseFormat: DATE_FORMAT_LONG_FNS });

  return getErknmPrescriptions(knm).filter(
    ({ decision }) =>
      Boolean(decision.guid) &&
      isPrescriptionNotCompleted(decision, isEnablePrescriptionCheck) &&
      hasViolationsForRepeatCheck(decision, startDate ?? undefined),
  );
};

/**
 * Опции поля "Предписание"
 */
export const extractPrescriptionOptions = (
  knm: IKnm,
  isEnablePrescriptionCheck: boolean,
  erknmStartDateString?: string,
): ISelectOption[] =>
  getPrescriptionsForRepeatCheck(knm, isEnablePrescriptionCheck, erknmStartDateString).map(prescription => ({
    id: getPrescriptionKey(prescription.decision),
    value: prescription.decision,
    label: getPrescriptionLabel(prescription),
  }));

export const getSelectedPrescriptionKeys = (rows: TPrescriptionRow[]): string[] =>
  rows.filter((row): row is ISelectOption => Boolean(row)).map(row => String(row.id));

/** Все предписания минус выбранные в других строках */
export const getPrescriptionOptionsForRow = (
  options: ISelectOption[],
  rows: TPrescriptionRow[],
  rowIndex: number,
): ISelectOption[] => {
  const selectedInOtherRows = getSelectedPrescriptionKeys(rows.filter((_, index) => index !== rowIndex));

  return options.filter(option => !selectedInOtherRows.includes(String(option.id)));
};
